import React, {
  useState,
  KeyboardEvent,
  ChangeEvent,
  SetStateAction,
  Dispatch,
  useEffect,
  useRef,
} from "react";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import "./MultiSearch.css";

interface MultiSearchProps {
  label: string;
  selectedItems: string[];
  placeholder?: string;
  value: Dispatch<SetStateAction<string[]>>; // Expecting a function to set string[] state
  parseType: "both" | "comma";
}

const MultiSearch: React.FC<MultiSearchProps> = ({
  placeholder,
  label,
  value,
  parseType = "both",
  selectedItems,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedChips, setSelectedChips] = useState<string[]>(selectedItems); // Use selectedChips consistently

  const inputRef = useRef<HTMLInputElement>(null); // Ref for the input element
  const opRef = useRef<OverlayPanel>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = (event: any) => {
    if (opRef.current) {
      if (isDropdownOpen) {
        opRef.current.hide();
      } else if (inputContainerRef.current) {
        opRef.current.show(event, inputContainerRef.current);
      }
    }
    setIsDropdownOpen((prevState) => {
      // Toggle the 'dropdown-open' class on the container
      if (inputContainerRef.current) {
        if (!prevState) {
          inputContainerRef.current.classList.add("dropdown-open");
        } else {
          inputContainerRef.current.classList.remove("dropdown-open");
        }
      }
      return !prevState;
    });
  };

  const onHide = () => {
    // Called when OverlayPanel hides
    setIsDropdownOpen(false); // Reset dropdown state
    // Remove the 'dropdown-open' class when the dropdown hides
    if (inputContainerRef.current) {
      inputContainerRef.current.classList.remove("dropdown-open");
    }
  };

  useEffect(() => {
    setSelectedChips(selectedItems);
  }, [selectedItems]);

  const parseInput = (input: string): string[] => {
    if (parseType === "both") {
      return input
        .split(/[,\s]+/) // Split by comma or whitespace
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
    } else if (parseType === "comma") {
      return input
        .split(",") // Split by comma only
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
    }
    return []; // Return empty array if input is invalid
  };

  const commitChips = (next: string[]) => {
    setSelectedChips(next);
    value(next);
  };

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim() !== "") {
      const newIds = parseInput(inputValue).filter(
        (id) => !selectedChips.includes(id)
      );
      if (newIds.length > 0) commitChips([...selectedChips, ...newIds]);
      setInputValue("");
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const removeChip = (chipIndex: number) => {
    commitChips(selectedChips.filter((_, i) => i !== chipIndex));
  };

  const addChipsFromInput = () => {
    if (inputValue.trim() !== "") {
      const newIds = parseInput(inputValue).filter(
        (id) => !selectedChips.includes(id)
      );
      if (newIds.length > 0) commitChips([...selectedChips, ...newIds]);
      setInputValue("");
    }
  };

  const clearChips = () => commitChips([]);

  return (
    <div className="flex-column gap-3" style={{ width: "17.58rem" }}>
      <label style={{ color: "grey", fontSize: "small" }}>{label}</label>
      <div className="multisearch-input-container" ref={inputContainerRef}>
        <InputText
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={onInputChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          className="chip-input-field"
          style={{ width: "100%" }} // Fill the available space inside the wrapper
        />
        <div className="button-group">
          <Button
            icon="pi pi-plus"
            onClick={addChipsFromInput}
            className="p-button-text add-button"
          />
          <Button
            icon={`pi pi-bars`}
            className="p-button-text chip-dropdown-button"
            onClick={toggleDropdown}
            size="small"
            severity="success"
            text
          />

          <Button
            icon="pi pi-replay"
            className="p-button-text clear-chips-button p-button-danger" // Style this in CSS
            onClick={clearChips}
          />
        </div>
      </div>
      <OverlayPanel
        ref={opRef}
        appendTo={typeof window !== "undefined" ? document.body : undefined}
        onHide={onHide}
        className="custom-chip-overlay"
      >
        <div className="chip-dropdown">
          {selectedChips.length > 0 ? (
            <div className="chip-container flex-row flex-wrap grid gap-1">
              {selectedChips.map((chipId, index) => (
                <Chip
                  key={chipId}
                  label={chipId}
                  removable
                  onRemove={() => {
                    removeChip(index);
                    return true;
                  }}
                  className="chip-item"
                  style={{ fontSize: "60%" }}
                />
              ))}
            </div>
          ) : (
            <p style={{ color: "grey", fontSize: "small", textAlign: "center" }}></p>
          )}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default MultiSearch;
